"use client";

import Link from "next/link";
import {
	Mail,
	Loader2,
	Check,
	Copy,
	ShieldCheck,
	RefreshCw,
} from "lucide-react";
import {
	InputOTP,
	InputOTPGroup,
	InputOTPSlot,
} from "@/components/ui/input-otp";
import { useSupportContactFlow } from "@/hooks/useSupportContactFlow";

type Variant = "landing" | "mobile";

export default function SupportContactInlineForm({
	variant,
	idPrefix = "inline",
}: {
	variant: Variant;
	idPrefix?: string;
}) {
	const flow = useSupportContactFlow({
		requireTermsAgreement: variant === "landing",
	});

	const {
		formData,
		setFormData,
		agreedToTerms,
		setAgreedToTerms,
		isSubmitting,
		step,
		caseNumber,
		verifyEmail,
		otp,
		setOtp,
		isVerifying,
		isResending,
		resendCooldown,
		emailVerified,
		copied,
		turnstileToken,
		turnstileContainerRef,
		handlePhoneChange,
		resetFlow,
		handleSubmit,
		handleVerifyOtp,
		handleResendOtp,
		handleCopyCaseNumber,
		clearFormAfterSuccess,
		skipVerifyToDone,
	} = flow;

	const isMobile = variant === "mobile";

	if (step === "done" && caseNumber) {
		if (isMobile) {
			return (
				<>
					<div className="bg-white rounded-2xl p-6 mt-4">
						<div className="flex flex-col gap-4">
							<div>
								<h3 className="text-[#16251c] text-xl font-semibold mb-2">
									{emailVerified
										? "You’re verified!"
										: "Message received"}
								</h3>
								<p className="text-gray-600 text-sm">
									{emailVerified
										? "Thanks for confirming your email. Verified requests get priority."
										: "We’ve received your message. Save your Case Number for reference."}
								</p>
							</div>
							<div className="bg-gray-50 rounded-lg p-4">
								<div className="flex flex-col gap-3">
									<div>
										<p className="text-gray-500 text-xs mb-1">
											Case Number
										</p>
										<p className="text-[#16251c] text-lg font-semibold font-mono break-all">
											{caseNumber}
										</p>
									</div>
									<button
										type="button"
										onClick={handleCopyCaseNumber}
										className="flex items-center justify-center gap-2 px-4 py-2 bg-[#A7FF5E] hover:bg-[#8ee63f] text-[#16251c] rounded-lg transition-colors w-full"
									>
										{copied ? (
											<>
												<Check className="w-4 h-4" />
												<span className="text-sm font-medium">
													Copied!
												</span>
											</>
										) : (
											<>
												<Copy className="w-4 h-4" />
												<span className="text-sm font-medium">
													Copy Case Number
												</span>
											</>
										)}
									</button>
								</div>
							</div>
							<button
								type="button"
								onClick={() => {
									clearFormAfterSuccess();
									resetFlow();
								}}
								className="w-full bg-[#a7ff5e] text-[#12301b] rounded-[28px] py-4 px-5 flex items-center justify-center gap-2 font-extrabold text-sm shadow-sm"
							>
								<span>Submit Another Request</span>
							</button>
						</div>
					</div>
				</>
			);
		}

		return (
			<div className="content-stretch flex flex-col gap-6 items-start relative shrink-0 w-full max-w-md mx-auto">
				<div className="bg-white rounded-2xl p-6 w-full">
					<div className="flex flex-col gap-4">
						<div>
							<h3 className="text-[#16251c] text-xl font-semibold mb-2">
								{emailVerified
									? "You’re verified!"
									: "Message received"}
							</h3>
							<p className="text-gray-600 text-sm">
								{emailVerified
									? "Thanks for confirming your email. Verified requests get priority in our queue."
									: "We’ve received your message. Save your Case Number for reference."}
							</p>
						</div>
						<div className="bg-gray-50 rounded-lg p-4">
							<div className="flex items-center justify-between gap-2">
								<div className="min-w-0">
									<p className="text-gray-500 text-xs mb-1">
										Case Number
									</p>
									<p className="text-[#16251c] text-lg font-semibold font-mono break-all">
										{caseNumber}
									</p>
								</div>
								<button
									type="button"
									onClick={handleCopyCaseNumber}
									className="flex items-center gap-2 px-4 py-2 bg-[#A7FF5E] hover:bg-[#8ee63f] text-[#16251c] rounded-lg transition-colors shrink-0"
								>
									{copied ? (
										<>
											<Check className="w-4 h-4" />
											<span className="text-sm font-medium">
												Copied!
											</span>
										</>
									) : (
										<>
											<Copy className="w-4 h-4" />
											<span className="text-sm font-medium">
												Copy
											</span>
										</>
									)}
								</button>
							</div>
						</div>
						<button
							type="button"
							onClick={() => {
								clearFormAfterSuccess();
								resetFlow();
							}}
							className="w-full bg-[#a7ff5e] h-12 rounded-[1.75rem] text-[#16251c] font-semibold hover:bg-[#8ee63f] transition-colors"
						>
							Submit Another Request
						</button>
					</div>
				</div>
			</div>
		);
	}

	if (step === "verify") {
		if (isMobile) {
			return (
				<>
					<div className="backdrop-blur-md bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.25)] rounded-3xl p-5 flex flex-col gap-5 mt-4">
						<h3 className="text-white text-lg font-bold">
							Verify your email
						</h3>
						<div className="space-y-3">
							<p className="text-[rgba(255,255,255,0.75)] text-sm">
								Enter the 6-digit code sent to{" "}
								<strong className="text-white">
									{verifyEmail}
								</strong>
							</p>
							<p className="flex items-start gap-2 rounded-xl bg-white/10 border border-white/15 px-3 py-2.5 text-sm font-semibold text-[#a7ff5e] leading-snug">
								<ShieldCheck
									className="w-5 h-5 shrink-0 mt-0.5"
									aria-hidden
								/>
								<span>
									Verified email gets priority support—we
									reply to verified requests first.
								</span>
							</p>
						</div>
						<div className="flex justify-center">
							<InputOTP
								maxLength={6}
								value={otp}
								onChange={(v) => setOtp(v)}
							>
								<InputOTPGroup className="gap-1.5">
									{[0, 1, 2, 3, 4, 5].map((i) => (
										<InputOTPSlot
											key={i}
											index={i}
											className="w-9 h-11 rounded-lg border-gray-400 bg-gray-50 text-[#12301b] text-base font-semibold"
										/>
									))}
								</InputOTPGroup>
							</InputOTP>
						</div>
						<button
							type="button"
							onClick={handleVerifyOtp}
							disabled={isVerifying || otp.length !== 6}
							className="w-full bg-[#a7ff5e] text-[#12301b] rounded-[28px] py-3.5 font-extrabold text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
						>
							{isVerifying ? (
								<Loader2 className="w-5 h-5 animate-spin" />
							) : (
								<ShieldCheck className="w-5 h-5" />
							)}
							Verify code
						</button>
						<div className="flex flex-col gap-2 pt-2 border-t border-white/10">
							<button
								type="button"
								onClick={handleResendOtp}
								disabled={isResending || resendCooldown > 0}
								className="text-sm text-[#a7ff5e] font-medium disabled:opacity-50 flex items-center justify-center gap-2"
							>
								<RefreshCw
									className={`w-4 h-4 ${isResending ? "animate-spin" : ""}`}
								/>
								{resendCooldown > 0
									? `Resend in ${resendCooldown}s`
									: "Resend code"}
							</button>
							<button
								type="button"
								onClick={skipVerifyToDone}
								className="text-xs text-white/50 hover:text-white/70"
							>
								Skip for now
							</button>
						</div>
					</div>
				</>
			);
		}

		return (
			<div className="w-full max-w-md mx-auto space-y-6">
				<div className="bg-white rounded-2xl p-6 space-y-6">
					<div>
						<h3 className="text-[#16251c] text-xl font-semibold mb-2">
							Verify your email
						</h3>
						<div className="space-y-3">
							<p className="text-gray-600 text-sm">
								We sent a code to{" "}
								<strong className="text-gray-900">
									{verifyEmail}
								</strong>
								. Enter it below.
							</p>
							<p className="flex items-start gap-2 rounded-xl bg-[#f0f7ed] border border-[#c5e0b8] px-3 py-2.5 text-sm font-semibold text-[#12301b] leading-snug">
								<ShieldCheck
									className="w-5 h-5 shrink-0 text-[#2d6b3f] mt-0.5"
									aria-hidden
								/>
								<span>
									Verified email gets priority support—we
									reply to verified requests first.
								</span>
							</p>
						</div>
					</div>
					<div className="flex flex-col items-center gap-4">
						<InputOTP
							maxLength={6}
							value={otp}
							onChange={(v) => setOtp(v)}
						>
							<InputOTPGroup className="gap-2">
								{[0, 1, 2, 3, 4, 5].map((i) => (
									<InputOTPSlot
										key={i}
										index={i}
										className="w-10 h-12 sm:w-12 sm:h-14 rounded-lg border-gray-300 text-lg font-semibold text-[#12301b]"
									/>
								))}
							</InputOTPGroup>
						</InputOTP>
						<button
							type="button"
							onClick={handleVerifyOtp}
							disabled={isVerifying || otp.length !== 6}
							className="w-full max-w-sm bg-[#12301b] text-white font-semibold py-3 px-6 rounded-full hover:bg-[#0f1b13] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
						>
							{isVerifying ? (
								<Loader2 className="w-5 h-5 animate-spin" />
							) : (
								<ShieldCheck className="w-5 h-5" />
							)}
							Verify code
						</button>
					</div>
					<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 border-t border-gray-100">
						<button
							type="button"
							onClick={handleResendOtp}
							disabled={isResending || resendCooldown > 0}
							className="inline-flex items-center justify-center gap-2 text-sm font-medium text-[#12301b] disabled:opacity-50"
						>
							<RefreshCw
								className={`w-4 h-4 ${isResending ? "animate-spin" : ""}`}
							/>
							{resendCooldown > 0
								? `Resend in ${resendCooldown}s`
								: "Resend code"}
						</button>
						<button
							type="button"
							onClick={skipVerifyToDone}
							className="text-sm text-gray-500 hover:text-gray-700 underline-offset-2 hover:underline"
						>
							Skip for now
						</button>
					</div>
				</div>
			</div>
		);
	}

	// form step — mobile
	if (isMobile) {
		return (
			<>
				<form
					onSubmit={handleSubmit}
					className="backdrop-blur-md bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.25)] rounded-3xl p-5 flex flex-col gap-6 mt-4"
				>
					<input
						type="text"
						placeholder="Name"
						value={formData.name}
						onChange={(e) =>
							setFormData({ ...formData, name: e.target.value })
						}
						className="bg-gray-50! rounded-2xl px-4 py-3 text-sm text-gray-900 outline-none border border-gray-300 focus:border-[#A7FF5E] focus:ring-2 focus:ring-[#A7FF5E] shadow-sm placeholder:text-gray-400"
						required
					/>
					<input
						type="email"
						placeholder="Email"
						value={formData.email}
						onChange={(e) =>
							setFormData({ ...formData, email: e.target.value })
						}
						className="bg-gray-50! rounded-2xl px-4 py-3 text-sm text-gray-900 outline-none border border-gray-300 focus:border-[#A7FF5E] focus:ring-2 focus:ring-[#A7FF5E] shadow-sm placeholder:text-gray-400"
						required
					/>
					<div className="bg-gray-50! rounded-2xl flex items-center border border-gray-300 focus-within:border-[#A7FF5E] focus-within:ring-2 focus-within:ring-[#A7FF5E] shadow-sm overflow-hidden">
						<div className="flex items-center gap-2 px-3 py-3 border-r border-gray-200 shrink-0">
							<span className="text-gray-500 text-sm">US</span>
							<svg
								className="w-5 h-5 text-gray-400"
								fill="none"
								viewBox="0 0 20 20"
							>
								<path
									d="M5 7.5L10 12.5L15 7.5"
									stroke="currentColor"
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth="1.67"
								/>
							</svg>
						</div>
						<input
							type="tel"
							placeholder="+1 (555) 000-0000"
							value={formData.phone}
							onChange={handlePhoneChange}
							maxLength={17}
							className="flex-1 px-3 py-3 text-sm text-gray-900 outline-none bg-transparent placeholder:text-gray-400"
						/>
					</div>
					<textarea
						placeholder="Leave us a message..."
						rows={4}
						value={formData.message}
						onChange={(e) =>
							setFormData({
								...formData,
								message: e.target.value,
							})
						}
						className="bg-gray-50! rounded-2xl px-4 py-3 text-sm text-gray-900 outline-none border border-gray-300 focus:border-[#A7FF5E] focus:ring-2 focus:ring-[#A7FF5E] shadow-sm resize-none placeholder:text-gray-400"
						required
					/>
					<p className="text-[rgba(255,255,255,0.4)] text-[10px] leading-relaxed">
						By submitting, you agree to our{" "}
						<Link
							href="/terms-of-service"
							className="text-[#a7ff5e] hover:underline"
						>
							terms
						</Link>{" "}
						and{" "}
						<Link
							href="/privacy-policy"
							className="text-[#a7ff5e] hover:underline"
						>
							Privacy Policy
						</Link>
						. We may email or text you about your request.
					</p>
					<div
						ref={turnstileContainerRef}
						className="flex min-h-[65px] w-full justify-center"
					/>
					<button
						type="submit"
						disabled={
							isSubmitting || !turnstileToken || !agreedToTerms
						}
						className="w-full bg-[#a7ff5e] text-[#12301b] rounded-[28px] py-4 px-5 flex items-center justify-center gap-2 font-extrabold text-sm shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
					>
						<Mail className="w-5 h-5 shrink-0" />
						<span>
							{isSubmitting ? "Submitting..." : "Contact Us"}
						</span>
					</button>
				</form>
			</>
		);
	}

	// form step — landing
	return (
		<>
			<form
				onSubmit={handleSubmit}
				className="w-full max-w-md mx-auto space-y-6"
			>
				<div>
					<label
						htmlFor={`${idPrefix}-name`}
						className="block text-sm font-medium text-gray-700 mb-2"
					>
						Name <span className="text-red-500">*</span>
					</label>
					<input
						type="text"
						id={`${idPrefix}-name`}
						name="name"
						placeholder="Name"
						value={formData.name}
						onChange={(e) =>
							setFormData({ ...formData, name: e.target.value })
						}
						required
						className="w-full px-4 py-3 bg-white rounded-lg border border-gray-300 focus:border-[#a7ff5e] focus:ring-2 focus:ring-[#a7ff5e] focus:outline-none text-gray-900 placeholder:text-gray-400 transition-colors"
					/>
				</div>
				<div>
					<label
						htmlFor={`${idPrefix}-email`}
						className="block text-sm font-medium text-gray-700 mb-2"
					>
						Email <span className="text-red-500">*</span>
					</label>
					<input
						type="email"
						id={`${idPrefix}-email`}
						name="email"
						placeholder="Email"
						value={formData.email}
						onChange={(e) =>
							setFormData({ ...formData, email: e.target.value })
						}
						required
						className="w-full px-4 py-3 bg-white rounded-lg border border-gray-300 focus:border-[#a7ff5e] focus:ring-2 focus:ring-[#a7ff5e] focus:outline-none text-gray-900 placeholder:text-gray-400 transition-colors"
					/>
				</div>
				<div>
					<label
						htmlFor={`${idPrefix}-phone`}
						className="block text-sm font-medium text-gray-700 mb-2"
					>
						Phone Number
					</label>
					<div className="flex items-center bg-gray-50 rounded-xl border border-gray-200 focus-within:border-[#A7FF5E] focus-within:ring-2 focus-within:ring-[#A7FF5E]/20 overflow-hidden transition-all">
						<div className="flex items-center gap-2 px-4 py-3 border-r border-gray-200 bg-white">
							<span className="text-gray-700 text-sm font-semibold">
								US
							</span>
							<svg
								className="w-4 h-4 text-gray-400"
								fill="none"
								viewBox="0 0 20 20"
							>
								<path
									d="M5 7.5L10 12.5L15 7.5"
									stroke="currentColor"
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth="1.67"
								/>
							</svg>
						</div>
						<input
							type="tel"
							id={`${idPrefix}-phone`}
							name="phone"
							placeholder="+1 (555) 000-0000"
							value={formData.phone}
							onChange={handlePhoneChange}
							maxLength={17}
							className="flex-1 px-4 py-3 text-gray-900 outline-none bg-gray-50 placeholder:text-gray-400"
						/>
					</div>
				</div>
				<div>
					<label
						htmlFor={`${idPrefix}-message`}
						className="block text-sm font-medium text-gray-700 mb-2"
					>
						Message <span className="text-red-500">*</span>
					</label>
					<textarea
						id={`${idPrefix}-message`}
						name="message"
						placeholder="Leave us a message..."
						value={formData.message}
						onChange={(e) =>
							setFormData({
								...formData,
								message: e.target.value,
							})
						}
						rows={5}
						required
						className="w-full px-4 py-3 bg-white rounded-lg border border-gray-300 focus:border-[#a7ff5e] focus:ring-2 focus:ring-[#a7ff5e] focus:outline-none text-gray-900 placeholder:text-gray-400 resize-none transition-colors"
					/>
				</div>
				<div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl border border-gray-100">
					<input
						type="checkbox"
						id={`${idPrefix}-terms`}
						name="terms"
						checked={agreedToTerms}
						onChange={(e) => setAgreedToTerms(e.target.checked)}
						required
						className="mt-1 w-5 h-5 text-[#A7FF5E] border-gray-300 rounded focus:ring-[#A7FF5E] focus:ring-2 cursor-pointer"
					/>
					<label
						htmlFor={`${idPrefix}-terms`}
						className="text-sm text-gray-600 cursor-pointer leading-relaxed"
					>
						I agree to{" "}
						<Link
							href="/terms-of-service"
							className="text-[#A7FF5E] hover:underline font-semibold"
							onClick={(e) => e.stopPropagation()}
						>
							terms & conditions
						</Link>{" "}
						and{" "}
						<Link
							href="/privacy-policy"
							className="text-[#A7FF5E] hover:underline font-semibold"
							onClick={(e) => e.stopPropagation()}
						>
							Privacy Policy
						</Link>{" "}
						provided by Tax Lien Vest. By providing my email address
						and phone number, I agree to receive text and email
						communication from Tax Lien Vest.
					</label>
				</div>

				{agreedToTerms && (
					<div
						ref={turnstileContainerRef}
						className="flex min-h-[65px] w-full justify-center "
					/>
				)}

				<button
					type="submit"
					disabled={isSubmitting || !turnstileToken || !agreedToTerms}
					className="w-full bg-[#A7FF5E] hover:bg-[#8ee63f] text-[#16251c] font-bold py-4 px-6 rounded-full flex items-center justify-center gap-2 transition-all disabled:opacity-70 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
				>
					{isSubmitting ? (
						<>
							<Loader2 className="w-5 h-5 animate-spin" />
							<span>Sending...</span>
						</>
					) : (
						<>
							<Mail className="w-5 h-5" />
							<span>Contact Us</span>
						</>
					)}
				</button>
			</form>
		</>
	);
}
